import { Node, mergeAttributes } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';
import type { MarkdownNodeSpec } from 'tiptap-markdown';

/**
 * The engagement blocks — `::: toggle`, `::: quiz` and `::: infographic` — kept
 * VERBATIM through an edit.
 *
 * Why this exists at all: the editor is TipTap, and TipTap only preserves what
 * it has a node for. A `:::` fence it does not recognise is parsed as ordinary
 * paragraphs and lists, and serialized back as ordinary paragraphs and lists —
 * so an owner who opens a generated article and changes one word elsewhere
 * silently destroys its quiz, and an infographic's inline SVG is dropped
 * outright. That is data loss on the likeliest path there is: the first edit of
 * a generated draft.
 *
 * So the block is an ATOM. Its source is captured as one opaque string and
 * written back byte for byte, which makes it impossible for the editor to
 * corrupt and impossible for the editor to improve. The owner sees the block's
 * source and can delete it whole; editing its content means editing the
 * markdown, which is the honest trade against losing it.
 *
 * The callouts, by contrast, ARE structured nodes (see ./callout) — they hold
 * ordinary block content that TipTap can round-trip safely, so nothing is
 * gained by freezing them.
 */

/** The kinds this node claims. Callouts are handled by ./callout and stay out. */
export const DIALECT_BLOCK_KINDS = ['toggle', 'quiz', 'infographic'] as const;
export type DialectBlockKind = (typeof DIALECT_BLOCK_KINDS)[number];

/** The block as the toolbar reads and writes it: a title line and a body. */
export type DialectBlockValue = {
  kind: DialectBlockKind;
  /** What follows the opening fence (a toggle's summary, a quiz's question). */
  title: string;
  /** Everything between the fences, verbatim. */
  source: string;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    dialectBlock: {
      /** Insert a new engagement block at the caret and select it. */
      insertDialectBlock: (value: DialectBlockValue) => ReturnType;
      /** Rewrite the selected block's title and body (used by the quiz writer). */
      setDialectBlock: (value: Pick<DialectBlockValue, 'title' | 'source'>) => ReturnType;
    };
  }
}

const OPEN_RE = new RegExp(`^:::\\s*(${DIALECT_BLOCK_KINDS.join('|')})\\b\\s*(.*)$`);
const CLOSE = ':::';

/** Minimal structural view of the markdown-it pieces used below. */
type BlockState = {
  src: string;
  bMarks: number[];
  eMarks: number[];
  tShift: number[];
  sCount: number[];
  blkIndent: number;
  line: number;
  lineMax: number;
  push: (type: string, tag: string, nesting: number) => Token;
};

type Token = { info: string; content: string; map: [number, number] | null; block: boolean };

type MarkdownIt = {
  block: { ruler: { before: (before: string, name: string, rule: unknown, options?: unknown) => void } };
  renderer: { rules: Record<string, (tokens: Token[], idx: number) => string> };
  utils: { escapeHtml: (value: string) => string };
};

const lineOf = (state: BlockState, line: number): string =>
  state.src.slice(state.bMarks[line] + state.tShift[line], state.eMarks[line]);

/**
 * A markdown-it block rule that swallows one fence whole.
 *
 * Registered BEFORE `fence` so a `:::` opener is claimed here rather than by
 * anything downstream, and it never touches a kind it does not own — a
 * `::: tip` still reaches the callout container untouched.
 */
const dialectBlockRule =
  () =>
  (state: BlockState, startLine: number, endLine: number, silent: boolean): boolean => {
    if (state.sCount[startLine] - state.blkIndent >= 4) return false;

    const open = OPEN_RE.exec(lineOf(state, startLine).trim());
    if (!open) return false;
    if (silent) return true;

    const [, kind, title] = open;
    const body: string[] = [];

    let line = startLine + 1;
    for (; line < endLine; line += 1) {
      if (lineOf(state, line).trim() === CLOSE) break;
      body.push(lineOf(state, line));
    }

    const token = state.push('dialect_block', 'div', 0);
    token.info = `${kind}\n${title.trim()}`;
    token.content = body.join('\n');
    token.map = [startLine, line];
    token.block = true;

    // Past the closing fence, or to the end when the author never wrote one.
    state.line = line < endLine ? line + 1 : endLine;

    return true;
  };

/**
 * The source travels as the element's TEXT, not as an attribute: an SVG carries
 * quotes and newlines, and an attribute would need a second escaping scheme on
 * top of the one the DOM already has.
 */
const renderDialectBlock =
  (md: MarkdownIt) =>
  (tokens: Token[], idx: number): string => {
    const [kind, title] = tokens[idx].info.split('\n');
    return (
      `<div data-dialect="${md.utils.escapeHtml(kind)}" data-dialect-title="${md.utils.escapeHtml(title ?? '')}">` +
      `<pre>${md.utils.escapeHtml(tokens[idx].content)}</pre></div>`
    );
  };

export const DialectBlock = Node.create({
  name: 'dialectBlock',

  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      kind: {
        default: 'toggle',
        parseHTML: (element: HTMLElement): string => element.getAttribute('data-dialect') ?? 'toggle',
      },
      title: {
        default: '',
        parseHTML: (element: HTMLElement): string => element.getAttribute('data-dialect-title') ?? '',
      },
      source: {
        default: '',
        parseHTML: (element: HTMLElement): string => element.querySelector('pre')?.textContent ?? '',
      },
      /**
       * Lumio is writing this block right now. Purely a state of the UI: the
       * markdown serializer never emits it, nothing in the dialect parses it
       * back, and it is set with `addToHistory: false` so an undo cannot
       * resurrect a wait that is over.
       */
      generating: {
        default: false,
        parseHTML: (): boolean => false,
        // Rendered by hand below as `data-generating`, so it must not also come
        // out as a bare `generating="false"` on the element.
        renderHTML: (): Record<string, string> => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-dialect]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-dialect': String(node.attrs.kind),
        'data-dialect-title': String(node.attrs.title),
        // Present only while it is true, so the stylesheet can select on the
        // attribute itself rather than on a value.
        'data-generating': node.attrs.generating ? '' : undefined,
      }),
      ['pre', {}, String(node.attrs.source)],
    ];
  },

  addCommands() {
    return {
      insertDialectBlock:
        (value: DialectBlockValue) =>
        ({ chain, state }) => {
          /*
           * BETWEEN blocks, never inside one. Inserting at the caret splits the
           * block it sits in, and the half left behind is an empty node of that
           * type: adding a quiz with the caret in a heading left an empty `##`
           * above it, which serializes into the article and renders as an empty
           * heading on the public page.
           *
           * `after(1)` is the position just past the top-level block the caret is
           * in, which is where a block of its own belongs anyway.
           */
          const { $from } = state.selection;
          const at = $from.depth > 0 ? $from.after(1) : $from.pos;

          return chain()
            .insertContentAt(at, { type: this.name, attrs: { ...value } })
            // Leave the new block SELECTED, not merely inserted. It is an atom
            // with no caret of its own, and the toolbar's block actions
            // (regenerate, edit, remove) read the selection to know which block
            // they are about — so an author who has just added a quiz can hit
            // "Write it with Lumio" without hunting for it first.
            .command(({ tr, dispatch }) => {
              if (!dispatch) return true;
              if (tr.doc.nodeAt(at)?.type.name !== this.name) return true;
              dispatch(tr.setSelection(NodeSelection.create(tr.doc, at)));
              return true;
            })
            .run();
        },

      setDialectBlock:
        (value: Pick<DialectBlockValue, 'title' | 'source'>) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, { ...value }),
    };
  },

  addStorage() {
    const markdown: MarkdownNodeSpec = {
      serialize(state, node) {
        const title = String(node.attrs.title);
        state.write(`::: ${String(node.attrs.kind)}${title ? ` ${title}` : ''}\n`);
        const source = String(node.attrs.source);
        if (source !== '') state.write(`${source}\n`);
        state.write(':::');
        state.closeBlock(node);
      },
      parse: {
        setup(markdownit) {
          const md = markdownit as unknown as MarkdownIt;
          md.block.ruler.before('fence', 'dialect_block', dialectBlockRule(), {
            alt: ['paragraph', 'blockquote', 'list'],
          });
          md.renderer.rules.dialect_block = renderDialectBlock(md);
        },
      },
    };
    return { markdown };
  },
});

export default DialectBlock;
